import {
    useImperativeHandle,
    useLayoutEffect,
    createContext,
    useCallback,
    forwardRef,
    useContext,
    useEffect,
    useState,
    Fragment,
    useMemo,
    useRef
} from "react";
import {
    Platform,
    Animated,
    View
} from "react-native";
import type IEmbeddedMenuProps from "./type";
import type {
    EmbeddedMenuWebPropsType,
    IEmbeddedMenuRef
} from "./type";
import stylesheet, {
    getEmbeddedMenuWidths,
    useStyles
} from "./stylesheet";
import MenuButton from "./components/menuButton";
import {
    getEmbeddedMenuUnsupportedProps
} from "./util";
import {
    NCoreUIKitEmbeddedMenu,
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../types";
import {
    useReducedMotion,
    getMotionTiming
} from "../../helpers/motion";
import {
    useActiveRouteName
} from "../../helpers/navigation";
import type {
    MenuButtonCollapseOverrides
} from "../menu/type";
import type {
    SidebarRootContextType,
    SidebarScrollStateType,
    SidebarContextType
} from "../sidebar/type";
import {
    PanelLeftClose as PanelLeftCloseIcon,
    PanelLeftOpen as PanelLeftOpenIcon
} from "lucide-react-native";
import {
    SafeAreaView
} from "react-native-safe-area-context";
import {
    getMenuButtonsWithCollapse,
    updateMenuButtonCollapse
} from "../menu/util";
import {
    SidebarRootContext,
    SidebarContext
} from "../sidebar/context";
import {
    getSidebarOverlaySide
} from "../sidebar/util";
import {
    warnOnce
} from "../../utils/development";
import AnimatedIcon from "../animatedIcon";
import SiteLogo from "../siteLogo";
import Sidebar from "../sidebar";
import Tooltip from "../tooltip";

const EmbeddedMenuToggleContext = createContext<boolean>(false);

const EmbeddedMenuToggleIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const isExpanded = useContext(EmbeddedMenuToggleContext);

    return <AnimatedIcon
        fromIcon={PanelLeftOpenIcon}
        toIcon={PanelLeftCloseIcon}
        isTransitioned={isExpanded}
        customColor={customColor}
        iconTransition="morph"
        color={color}
        size={size}
    />;
};

const EmbeddedMenu: RefForwardingComponent<IEmbeddedMenuRef, IEmbeddedMenuProps> = ({
    renderHeaderSeparator: renderHeaderSeparatorProp,
    renderHeaderSeperator: renderHeaderSeperatorProp,
    isWorkWithSafeAreaView = true,
    isWorkWithHeaderSpace = true,
    isWorkWithAnimation = true,
    renderFooter: RenderFooter,
    renderHeader: RenderHeader,
    isShowSiteLogo = true,
    isAutoClosedOnClosed,
    isShowCollapseButton,
    isShowCollabsButton,
    close: closeAction,
    backgroundColor,
    customLocalize,
    renderSiteLogo,
    isCollapsible,
    siteLogoProps,
    isAutoClosed,
    customTheme,
    headerSpace,
    isCollapse,
    navigation,
    maxWidth,
    minWidth,
    onClosed,
    onOpened,
    buttons,
    onClose,
    onOpen,
    style,
    logo,
    id,
    ...props
}, ref) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        durations,
        borders,
        configs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isReduceMotionEnabled = useReducedMotion();

    const isCurrentShowCollapseButton = isShowCollapseButton ?? isShowCollabsButton ?? true;

    const currentRenderHeaderSeparator = renderHeaderSeparatorProp ?? renderHeaderSeperatorProp;

    const menuTitle = localize("menu");

    const activeRouteName = useActiveRouteName({
        navigation
    });

    const [
        isActive,
        setIsActive
    ] = useState(isCollapse ?? false);

    const [
        isWidthAnimating,
        setIsWidthAnimating
    ] = useState(false);

    const [
        collapseOverrides,
        setCollapseOverrides
    ] = useState<MenuButtonCollapseOverrides>({});

    const [
        scrollState,
        setScrollState
    ] = useState<SidebarScrollStateType>({
        isOverflow: false,
        isScrolled: false
    });

    const currentWidths = getEmbeddedMenuWidths({
        controlHeights,
        shapeConfigs,
        maxWidth,
        minWidth,
        spaces
    });

    const widthTiming = getMotionTiming({
        intent: isActive ? "expand" : "collapse",
        isReduceMotionEnabled,
        durations
    });

    const widthDuration = isWorkWithAnimation ? widthTiming.duration : 0;

    const animatedWidth = useRef(new Animated.Value(isActive ? currentWidths.maxWidth : currentWidths.minWidth)).current;

    const previousIsActiveRef = useRef(isActive);

    const onOpenedRef = useRef(onOpened);

    const onClosedRef = useRef(onClosed);

    const currentButtons = useMemo(() => getMenuButtonsWithCollapse({
        buttons: buttons ?? [],
        collapseOverrides
    }), [
        collapseOverrides,
        buttons
    ]);

    const {
        logoContainer: logoContainerDynamicStyle,
        container: containerDynamicStyle,
        section: sectionDynamicStyle,
        body: bodyDynamicStyle
    } = useStyles({
        maxWidth: currentWidths.maxWidth,
        minWidth: currentWidths.minWidth,
        isExpanded: isActive,
        backgroundColor,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        borders,
        colors,
        spaces
    });

    const webProps: EmbeddedMenuWebPropsType = Platform.OS === "web" ? {
        dir: direction
    } : {};

    useEffect(() => {
        if(isShowCollabsButton === undefined) {
            return;
        }

        warnOnce({
            message: "EmbeddedMenu: \"isShowCollabsButton\" is deprecated and will be removed in the next major version. Use \"isShowCollapseButton\" instead."
        });
    }, [
        isShowCollabsButton
    ]);

    useEffect(() => {
        if(renderHeaderSeperatorProp === undefined) {
            return;
        }

        warnOnce({
            message: "EmbeddedMenu: \"renderHeaderSeperator\" is deprecated and will be removed in the next major version. Use \"renderHeaderSeparator\" instead."
        });
    }, [
        renderHeaderSeperatorProp
    ]);

    useEffect(() => {
        if(isCollapsible === undefined) {
            return;
        }

        warnOnce({
            message: "EmbeddedMenu: \"isCollapsible\" currently has no effect. Set \"isCollapsible\" on the entries of \"buttons\" to make a group collapsible, and \"isShowCollapseButton\" to show or hide the collapse button."
        });
    }, [
        isCollapsible
    ]);

    useEffect(() => {
        if(logo === undefined) {
            return;
        }

        warnOnce({
            message: "EmbeddedMenu: \"logo\" currently has no effect. Use \"siteLogoProps\" or \"renderSiteLogo\" to set up the header logo."
        });
    }, [
        logo
    ]);

    useEffect(() => {
        if(isAutoClosedOnClosed === undefined || NCoreUIKitEmbeddedMenu.get({
            id
        })) {
            return;
        }

        warnOnce({
            message: "EmbeddedMenu: \"isAutoClosedOnClosed\" currently has no effect on a menu that is not registered with NCoreUIKitEmbeddedMenu.load. Update your own state in \"onClosed\" instead."
        });
    }, [
        isAutoClosedOnClosed
    ]);

    useEffect(() => {
        getEmbeddedMenuUnsupportedProps({
            buttons: buttons ?? []
        }).forEach((key) => {
            warnOnce({
                message: `EmbeddedMenu: "props.${key}" on buttons currently has no effect. Rows are drawn with Sidebar parts; use "style", "icon", "badge" or "isShowRedirectIcon" instead.`
            });
        });
    }, [
        buttons
    ]);

    useEffect(() => {
        onOpenedRef.current = onOpened;

        onClosedRef.current = onClosed;
    });

    useImperativeHandle(
        ref,
        () => ({
            close: handleClose,
            open: handleOpen
        })
    );

    useEffect(() => {
        if(isCollapse === undefined) {
            return;
        }

        setIsActive(isCollapse);
    }, [
        isCollapse
    ]);

    useLayoutEffect(() => {
        if(previousIsActiveRef.current === isActive) {
            return;
        }

        previousIsActiveRef.current = isActive;

        if(isActive) {
            if(onOpen) onOpen({
                id
            });
        } else {
            if(onClose) onClose({
                id
            });
        }

        if(!widthDuration) {
            onWidthEnd(isActive);

            return;
        }

        if(!isWidthAnimating) {
            animatedWidth.setValue(isActive ? currentWidths.minWidth : currentWidths.maxWidth);

            setIsWidthAnimating(true);
        }

        const animation = Animated.timing(animatedWidth, {
            toValue: isActive ? currentWidths.maxWidth : currentWidths.minWidth,
            easing: widthTiming.easing,
            duration: widthDuration,
            useNativeDriver: false
        });

        animation.start(({
            finished
        }) => {
            if(!finished) {
                return;
            }

            setIsWidthAnimating(false);

            onWidthEnd(isActive);
        });

        return () => {
            animation.stop();
        };
    }, [
        isActive
    ]);

    const onWidthEnd = (isActiveEnd: boolean) => {
        if(isActiveEnd) {
            if(onOpenedRef.current) onOpenedRef.current({
                id
            });

            return;
        }

        if(onClosedRef.current) onClosedRef.current({
            id
        });
    };

    const handleOpen = useCallback(() => {
        setIsActive(true);

        if(NCoreUIKitEmbeddedMenu.get({
            id
        })) {
            NCoreUIKitEmbeddedMenu.open({
                id
            });
        }
    }, [
        id
    ]);

    const handleClose = useCallback(() => {
        setIsActive(false);

        if(NCoreUIKitEmbeddedMenu.get({
            id
        })) {
            NCoreUIKitEmbeddedMenu.close({
                id
            });
        }
    }, [
        id
    ]);

    const handleScrollState = useCallback((nextScrollState: SidebarScrollStateType) => {
        setScrollState((currentState) => {
            if(currentState.isOverflow === nextScrollState.isOverflow && currentState.isScrolled === nextScrollState.isScrolled) {
                return currentState;
            }

            return nextScrollState;
        });
    }, []);

    const sidebarContext = useMemo<SidebarContextType>(() => ({
        handleToggle: isActive ? handleClose : handleOpen,
        state: isActive ? "expanded" : "collapsed",
        handleOpenChange: (isOpen: boolean) => {
            if(isOpen) {
                handleOpen();

                return;
            }

            handleClose();
        },
        variant: "sidebar",
        isOpen: isActive,
        customLocalize,
        isPhone: false,
        shortcut: null,
        sidebarId: id,
        customTheme
    }), [
        customLocalize,
        customTheme,
        handleClose,
        handleOpen,
        isActive,
        id
    ]);

    const rootContext = useMemo<SidebarRootContextType>(() => ({
        isScrolled: currentRenderHeaderSeparator ? false : scrollState.isScrolled,
        isOverflow: scrollState.isOverflow,
        collapseBehaviour: "icon",
        isCollapsed: !isActive,
        variant: "sidebar",
        handleScrollState,
        title: menuTitle,
        customLocalize,
        isPhone: false,
        customTheme,
        side: "left"
    }), [
        scrollState.isOverflow,
        scrollState.isScrolled,
        currentRenderHeaderSeparator,
        handleScrollState,
        customLocalize,
        customTheme,
        menuTitle,
        isActive
    ]);

    const handleMenuButtonCollapse = ({
        depthMap,
        status
    }: {
        depthMap: Array<number>;
        status: boolean;
    }) => {
        updateMenuButtonCollapse({
            store: NCoreUIKitEmbeddedMenu,
            setCollapseOverrides,
            depthMap,
            status,
            id
        });
    };

    const renderToggle = () => {
        if(!isCurrentShowCollapseButton) {
            return null;
        }

        return <EmbeddedMenuToggleContext.Provider
            value={isActive}
        >
            <Tooltip
                isOpen={isActive ? false : undefined}
                customLocalize={customLocalize}
                title={localize("expand-menu")}
                side={getSidebarOverlaySide({
                    side: "left",
                    direction
                })}
                customTheme={customTheme}
            >
                <Sidebar.MenuButton
                    title={isActive ? localize("collapse-menu") : localize("expand-menu")}
                    onPress={isActive ? handleClose : handleOpen}
                    icon={EmbeddedMenuToggleIcon}
                    aria-expanded={isActive}
                    tooltip={false}
                    size="large"
                />
            </Tooltip>
        </EmbeddedMenuToggleContext.Provider>;
    };

    const renderLogo = () => {
        if(renderSiteLogo) {
            return renderSiteLogo({
                isActive
            });
        }

        if(isActive) {
            return <SiteLogo
                customLocalize={customLocalize}
                customTheme={customTheme}
                size="medium"
                {...siteLogoProps}
            />;
        }

        const siteLogoTitle = siteLogoProps?.titleKey ? localize(siteLogoProps.titleKey) : siteLogoProps?.title;

        return <SiteLogo
            customLocalize={customLocalize}
            customTheme={customTheme}
            size="medium"
            {...siteLogoProps}
            accessibilityLabel={siteLogoProps?.accessibilityLabel ?? (siteLogoProps?.isWorkWithAction && siteLogoTitle ? localize("site-logo-home", [
                siteLogoTitle
            ]) : undefined)}
            isWorkWithFlex1={false}
            titleKey={undefined}
            title={undefined}
        />;
    };

    const renderHeader = () => {
        if(RenderHeader) {
            return <RenderHeader
                maxWidth={currentWidths.maxWidth}
                minWidth={currentWidths.minWidth}
                isCollapse={isActive}
            />;
        }

        if(!isShowSiteLogo || (!isActive && !renderSiteLogo && siteLogoProps?.isShowIcon === false)) {
            return null;
        }

        return <Fragment>
            <Sidebar.Header
                style={sectionDynamicStyle}
            >
                <View
                    style={[
                        stylesheet.logoContainer,
                        logoContainerDynamicStyle
                    ]}
                >
                    {renderLogo()}
                </View>
            </Sidebar.Header>
            {currentRenderHeaderSeparator ? currentRenderHeaderSeparator() : null}
        </Fragment>;
    };

    const renderButtons = () => {
        if(!currentButtons.length) {
            return null;
        }

        return <Sidebar.Group
            style={sectionDynamicStyle}
        >
            <Sidebar.Menu>
                {
                    currentButtons.map((buttonItem, buttonIndex) => {
                        return <MenuButton
                            closeAction={closeAction ?? (isAutoClosed ? handleClose : undefined)}
                            updateMenuButtonCollapse={handleMenuButtonCollapse}
                            key={`embedded-menu-button-${id}-${buttonIndex}`}
                            activeRouteName={activeRouteName}
                            customLocalize={customLocalize}
                            customTheme={customTheme}
                            isMenuExpanded={isActive}
                            buttonItem={buttonItem}
                            navigation={navigation}
                            depthMap={[
                                buttonIndex
                            ]}
                            id={id}
                        />;
                    })
                }
            </Sidebar.Menu>
        </Sidebar.Group>;
    };

    const renderFooter = () => {
        if(!RenderFooter && !isCurrentShowCollapseButton) {
            return null;
        }

        return <Sidebar.Footer
            style={sectionDynamicStyle}
        >
            {
                RenderFooter ?
                    <RenderFooter
                        maxWidth={currentWidths.maxWidth}
                        minWidth={currentWidths.minWidth}
                        isCollapse={isActive}
                    />
                :
                    null
            }
            {renderToggle()}
        </Sidebar.Footer>;
    };

    const renderSections = () => {
        return <Fragment>
            {renderHeader()}
            <Sidebar.Content
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
            >
                {renderButtons()}
            </Sidebar.Content>
            {renderFooter()}
        </Fragment>;
    };

    const renderSafeAreaContext = () => {
        if(!isWorkWithSafeAreaView) {
            return <Fragment>
                {
                    isWorkWithHeaderSpace && (configs.headerSpace || headerSpace) ?
                        <View
                            style={[
                                stylesheet.headerSpacer,
                                {
                                    height: headerSpace ? headerSpace : configs.headerSpace
                                }
                            ]}
                        />
                    :
                        null
                }
                {renderSections()}
            </Fragment>;
        }

        return <SafeAreaView
            style={[
                stylesheet.safeAreaView
            ]}
        >
            {renderSections()}
        </SafeAreaView>;
    };

    return <SidebarContext.Provider
        value={sidebarContext}
    >
        <SidebarRootContext.Provider
            value={rootContext}
        >
            <Animated.View
                {...props}
                {...webProps}
                style={[
                    style,
                    stylesheet.container,
                    containerDynamicStyle,
                    {
                        width: isWidthAnimating ? animatedWidth : isActive ? currentWidths.maxWidth : currentWidths.minWidth
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.body,
                        bodyDynamicStyle
                    ]}
                >
                    {renderSafeAreaContext()}
                </View>
            </Animated.View>
        </SidebarRootContext.Provider>
    </SidebarContext.Provider>;
};
export default forwardRef(EmbeddedMenu);
